import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_ME } from '../Config/ApiUrlConstant';
import { apiJson } from '../utils/apiClient';
import { AUTH_EXPIRED_EVENT } from '../utils/axiosInstance';

const AuthContext = createContext();

const parseJwtPayload = (token) => {
  const [, payload] = token.split('.');
  if (!payload) {
    return null;
  }

  const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = (4 - (normalizedPayload.length % 4)) % 4;
  const paddedPayload = `${normalizedPayload}${'='.repeat(paddingLength)}`;
  return JSON.parse(window.atob(paddedPayload));
};

const decodeTokenUser = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    const decodedPayload = parseJwtPayload(token);
    if (!decodedPayload) {
      return null;
    }

    return {
      id: decodedPayload.userId || null,
      username: decodedPayload.username || '',
      role_key: decodedPayload.role_key || '',
    };
  } catch (error) {
    return null;
  }
};

const loadStoredUser = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem('auth_user') || 'null');
    if (storedUser?.role_key) {
      return storedUser;
    }

    return decodeTokenUser();
  } catch (error) {
    return decodeTokenUser();
  }
};

const loadStoredPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem('auth_permissions') || '[]');
  } catch (error) {
    return [];
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState(loadStoredUser);
  const [permissions, setPermissions] = useState(loadStoredPermissions);
  const [selectedBranchId, setSelectedBranchId] = useState(() => localStorage.getItem('selected_branch_id') || '');

  const selectBranch = (branchId) => {
    const value = branchId || '';
    localStorage.setItem('selected_branch_id', value);
    setSelectedBranchId(value);
  };

  const login = (token, nextUser, nextPermissions = []) => {
    const fallbackUser = nextUser || decodeTokenUser();
    const normalizedUser = fallbackUser || null;
    localStorage.setItem('token', token);
    localStorage.setItem('auth_user', JSON.stringify(normalizedUser));
    localStorage.setItem('auth_permissions', JSON.stringify(nextPermissions || []));
    setUser(normalizedUser);
    setPermissions(nextPermissions || []);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_permissions');
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('selected_branch_id');
    setSelectedBranchId('');
    setIsAuthenticated(false);
    window.location.href = '/login'; // Redirect immediately
  };

  const canAccess = useCallback((moduleKey, action = 'view') => {
    const resolvedUser = user || decodeTokenUser();

    if (!resolvedUser) {
      return false;
    }

    if (
      resolvedUser.role_key === 'super_admin' ||
      String(resolvedUser.id) === '1' ||
      resolvedUser.username === 'superadmin-thirdwave'
    ) {
      return true;
    }

    const modulePermission = permissions.find((permission) => permission.module_key === moduleKey);
    return Boolean(modulePermission?.[`can_${action}`]);
  }, [permissions, user]);

  useEffect(() => {
    if (localStorage.getItem('token') && (!user || !user.role_key)) {
      const tokenUser = decodeTokenUser();
      if (tokenUser?.role_key) {
        localStorage.setItem('auth_user', JSON.stringify(tokenUser));
        setUser(tokenUser);
      }
    }
  }, [user]);

  useEffect(() => {
    const refreshUserFromServer = async () => {
      if (!localStorage.getItem('token')) {
        return;
      }

      try {
        const { data } = await apiJson(API_ME, {
          method: 'POST',
          body: JSON.stringify({}),
        });

        if (data?.status && data?.data) {
          localStorage.setItem('auth_user', JSON.stringify(data.data));
          localStorage.setItem('auth_permissions', JSON.stringify(data.data.permissions || []));
          setUser(data.data);
          setPermissions(data.data.permissions || []);
          setIsAuthenticated(true);
        }
      } catch (error) {
      }
    };

    void refreshUserFromServer();
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      if (localStorage.getItem('token')) {
        logout();
      }
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = (url, options = {}) => {
      const selectedBranchId = localStorage.getItem('selected_branch_id');
      const isJsonRequest = options.body && typeof options.body === 'string'
        && String(options.headers?.['Content-Type'] || options.headers?.['content-type'] || '').includes('application/json');
      if (!selectedBranchId || !isJsonRequest) return originalFetch(url, options);
      try {
        const body = JSON.parse(options.body);
        return originalFetch(url, { ...options, body: JSON.stringify({ ...body, branch_id: body.branch_id || selectedBranchId }) });
      } catch (error) {
        return originalFetch(url, options);
      }
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  const value = useMemo(() => ({
    isAuthenticated,
    user,
    permissions,
    login,
    logout,
    canAccess,
    selectedBranchId,
    selectBranch,
  }), [canAccess, isAuthenticated, permissions, selectedBranchId, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
